import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { DollarSign, CarFront, Percent, Clock, Plus, Pencil, Trash2, Users, Upload, Loader2, FileText } from "lucide-react";

const CATEGORIES = ["Luxury Sedans", "Premium SUVs", "Exotics", "Electric"];
const EMPTY = { name: "", category: "Luxury Sedans", daily_rate: 100, seats: 5, transmission: "Automatic", fuel: "Petrol", luggage: 2, zero_to_sixty: "6.0s", engine: "", year: "", make: "", model: "", color: "", plate_number: "", vin: "", image_url: "", description: "", available: true };

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [cars, setCars] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/cars").then((r) => setCars(r.data));
    api.get("/bookings").then((r) => setBookings(r.data));
    api.get("/admin/customers").then((r) => setCustomers(r.data));
  };
  useEffect(loadAll, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (car) => { setEditing(car); setForm(car); setDialogOpen(true); };

  const saveCar = async () => {
    const payload = { ...form, daily_rate: Number(form.daily_rate), seats: Number(form.seats), luggage: Number(form.luggage) };
    try {
      if (editing) await api.put(`/cars/${editing.id}`, payload);
      else await api.post("/cars", payload);
      toast.success(editing ? "Vehicle updated" : "Vehicle added");
      setDialogOpen(false);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteCar = async (id) => {
    try { await api.delete(`/cars/${id}`); toast.success("Vehicle removed"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleAvailable = async (car) => {
    await api.put(`/cars/${car.id}`, { available: !car.available });
    loadAll();
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: `${BACKEND_URL}${data.url}` }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const setBookingStatus = async (id, status) => {
    await api.put(`/bookings/${id}/status`, { status });
    toast.success("Booking updated");
    loadAll();
  };

  const downloadContract = async (b) => {
    setDownloadingId(b.id);
    try {
      const res = await api.get(`/bookings/${b.id}/contract`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `AAR-contract-${b.id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate contract. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const metrics = [
    { icon: <DollarSign size={18} />, label: "Total Revenue", value: `$${(stats?.revenue ?? 0).toLocaleString()}` },
    { icon: <CarFront size={18} />, label: "Active Rentals", value: stats?.active_rentals ?? 0 },
    { icon: <Percent size={18} />, label: "Fleet Occupancy", value: `${stats?.fleet_occupancy ?? 0}%` },
    { icon: <Clock size={18} />, label: "Pending Returns", value: stats?.pending_returns ?? 0 },
  ];

  return (
    <div className="App bg-slate-50 min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-emerald-950" data-testid="admin-title">Command Center</h1>
        <p className="mt-2 text-slate-500">Manage your fleet, bookings and customers.</p>

        <div className="mt-8 grid gap-4 grid-cols-2 lg:grid-cols-4" data-testid="admin-metrics">
          {metrics.map((m, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200/70 p-5">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">{m.icon}</span>
              <p className="mt-3 text-2xl font-display font-extrabold text-emerald-950">{m.value}</p>
              <p className="text-sm text-slate-400">{m.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="fleet" className="mt-10">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="fleet" data-testid="tab-fleet">Fleet ({cars.length})</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings ({bookings.length})</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers">Customers ({customers.length})</TabsTrigger>
          </TabsList>

          {/* Fleet */}
          <TabsContent value="fleet" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button onClick={openNew} className="bg-emerald-900 hover:bg-emerald-800 rounded-full" data-testid="add-car-button">
                <Plus size={16} className="mr-1.5" /> Add vehicle
              </Button>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/70 overflow-hidden">
              {cars.map((c, i) => (
                <div key={c.id} className="flex items-center gap-4 p-4 border-b border-slate-100 last:border-0" data-testid={`admin-car-${i}`}>
                  <img src={c.image_url} alt="" className="h-14 w-20 object-cover rounded-lg bg-slate-100" />
                  <div className="flex-1 min-w-[140px]">
                    <p className="font-semibold text-emerald-950">{c.name}</p>
                    <p className="text-sm text-slate-400">{c.category} · ${c.daily_rate}/day</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={c.available} onCheckedChange={() => toggleAvailable(c)} data-testid={`toggle-available-${i}`} />
                    <span className="text-xs text-slate-400 w-16">{c.available ? "Available" : "Rented"}</span>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-car-${i}`}><Pencil size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => deleteCar(c.id)} data-testid={`delete-car-${i}`} className="text-red-500 hover:text-red-600"><Trash2 size={16} /></Button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Bookings */}
          <TabsContent value="bookings" className="mt-6">
            <div className="bg-white rounded-2xl border border-slate-200/70 overflow-hidden" data-testid="admin-bookings">
              {bookings.length === 0 ? (
                <p className="p-10 text-center text-slate-400">No bookings yet.</p>
              ) : bookings.map((b, i) => (
                <div key={b.id} className="flex items-center gap-4 p-4 border-b border-slate-100 last:border-0 flex-wrap" data-testid={`admin-booking-${i}`}>
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-semibold text-emerald-950">{b.car_name}</p>
                    <p className="text-sm text-slate-400">{b.user_name} · {b.user_email}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{b.pickup_date} → {b.return_date} · {b.pickup_location}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-display font-bold text-emerald-900">${b.total.toFixed(2)}</p>
                    <Badge className={b.payment_status === "paid" ? "bg-emerald-100 text-emerald-700 border-0" : "bg-slate-100 text-slate-500 border-0"}>{b.payment_status}</Badge>
                  </div>
                  <Select value={b.status} onValueChange={(v) => setBookingStatus(b.id, v)}>
                    <SelectTrigger className="w-36" data-testid={`booking-status-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["pending", "confirmed", "active", "completed", "cancelled"].map((s) => (
                        <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={() => downloadContract(b)} disabled={downloadingId === b.id} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-full" data-testid={`admin-contract-${i}`}>
                    {downloadingId === b.id ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <FileText size={15} className="mr-1.5" />}
                    Contract
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Customers */}
          <TabsContent value="customers" className="mt-6">
            <div className="bg-white rounded-2xl border border-slate-200/70 overflow-hidden" data-testid="admin-customers">
              {customers.length === 0 ? (
                <p className="p-10 text-center text-slate-400">No customers yet.</p>
              ) : customers.map((c, i) => (
                <div key={c.id} className="flex items-center gap-4 p-4 border-b border-slate-100 last:border-0" data-testid={`admin-customer-${i}`}>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><Users size={16} /></span>
                  <div className="flex-1">
                    <p className="font-semibold text-emerald-950">{c.name}</p>
                    <p className="text-sm text-slate-400">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Car dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="car-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit vehicle" : "Add vehicle"}</DialogTitle>
            <DialogDescription>Fill in the vehicle details below and save to update your fleet.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name" className="col-span-2"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="car-form-name" /></Field>
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="car-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Daily rate ($)"><Input type="number" value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: e.target.value })} data-testid="car-form-rate" /></Field>
            <Field label="Seats"><Input type="number" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} /></Field>
            <Field label="Luggage"><Input type="number" value={form.luggage} onChange={(e) => setForm({ ...form, luggage: e.target.value })} /></Field>
            <Field label="Transmission"><Input value={form.transmission} onChange={(e) => setForm({ ...form, transmission: e.target.value })} /></Field>
            <Field label="Fuel"><Input value={form.fuel} onChange={(e) => setForm({ ...form, fuel: e.target.value })} /></Field>
            <Field label="0-60 mph"><Input value={form.zero_to_sixty} onChange={(e) => setForm({ ...form, zero_to_sixty: e.target.value })} /></Field>
            <Field label="Engine"><Input value={form.engine} onChange={(e) => setForm({ ...form, engine: e.target.value })} /></Field>
            <Field label="Year"><Input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="car-form-year" /></Field>
            <Field label="Make"><Input value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} data-testid="car-form-make" /></Field>
            <Field label="Model"><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="car-form-model" /></Field>
            <Field label="Color"><Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="car-form-color" /></Field>
            <Field label="Plate #"><Input value={form.plate_number} onChange={(e) => setForm({ ...form, plate_number: e.target.value })} data-testid="car-form-plate" /></Field>
            <Field label="VIN" className="col-span-2"><Input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} data-testid="car-form-vin" /></Field>
            <Field label="Vehicle photo" className="col-span-2">
              <div className="flex items-center gap-4">
                <div className="h-20 w-28 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                  {form.image_url ? (
                    <img src={form.image_url} alt="preview" className="h-full w-full object-cover" data-testid="car-form-image-preview" />
                  ) : (
                    <CarFront className="text-slate-300" size={22} />
                  )}
                </div>
                <div className="flex-1">
                  <label className="inline-flex items-center gap-2 cursor-pointer text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg px-4 py-2.5 transition-colors" data-testid="upload-photo-label">
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    {uploading ? "Uploading…" : "Upload photo"}
                    <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="car-form-image-input" />
                  </label>
                  <p className="mt-1.5 text-xs text-slate-400">JPEG, PNG, WEBP or GIF · up to 8MB</p>
                </div>
              </div>
            </Field>
            <Field label="Description" className="col-span-2"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button onClick={saveCar} className="bg-emerald-900 hover:bg-emerald-800" data-testid="save-car-button">{editing ? "Save changes" : "Add vehicle"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <Label className="text-xs text-slate-500">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
